import { useEffect, useState } from "react";
import { config } from "../config";

export type HistoryWindow = "1h" | "6h" | "24h";
export type AvailabilityRange = "1h" | "1d" | "7d";
export type SegmentState = "up" | "down" | "partial" | "nodata" | "reboot";

export interface SeriesPoint {
  t: number;
  v: number;
}

export interface TimelineSegment {
  t: number;
  state: SegmentState;
}

export interface Availability {
  uptimePct: number;
  offlineEvents: number;
  reboots: number;
  downtimeMs: number;
  currentlyOffline: boolean;
  samples: number;
  timeline: TimelineSegment[];
}

export interface DeviceHistory {
  series: Record<string, SeriesPoint[]>;
  availability: Availability | null;
}

const REFRESH_MS = 20_000;

export function useDeviceHistory(
  deviceId: string,
  window: HistoryWindow,
  range: AvailabilityRange,
  // Changes whenever the live up/down status flips. Passing it here makes the
  // history refetch immediately on a down/up event instead of waiting for the
  // next poll, so the timeline's historical data catches up fast.
  liveKey?: string,
): DeviceHistory {
  const [history, setHistory] = useState<DeviceHistory>({
    series: {},
    availability: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [telRes, relRes] = await Promise.all([
          fetch(
            `${config.apiBase}/history/telemetry?device=${encodeURIComponent(deviceId)}&window=${window}`,
          ),
          fetch(
            `${config.apiBase}/history/reliability?device=${encodeURIComponent(deviceId)}&since=${range}`,
          ),
        ]);
        if (cancelled) return;
        const series = telRes.ok ? await telRes.json() : {};
        const availability = relRes.ok ? await relRes.json() : null;
        if (!cancelled) setHistory({ series, availability });
      } catch {
        // keep the previous data on a transient fetch failure
      }
    }

    load();
    const id = globalThis.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      globalThis.clearInterval(id);
    };
  }, [deviceId, window, range, liveKey]);

  return history;
}
